/**
 * Global data-scope context for the Marketer View redesign (SYR-68).
 *
 * Promotes the old buried "Data Selection" screen into an app-wide control:
 * one period / cohort / country / brand selection that governs the data on
 * EVERY destination in the new shell (Briefing, Detail, Responses, Benchmarks,
 * Expert, Action Plan).
 *
 * Source of truth stays the SAME localStorage key the rest of the dashboard
 * uses — `brandEquityFilters_${userId}` — so the marketer view and the legacy
 * `/dashboard/*` analytics pages compute Brand Equity on identical filters and
 * therefore always show the same numbers. Writing back on every change keeps
 * the two worlds in sync.
 *
 * The active brand is resolved from the `:brandHash` route param (the URL is
 * the source of truth for brand); period/cohort/country live in this context's
 * state, hydrated from and persisted to localStorage.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
  useGetBrandsQuery,
  useGetCohortsByBrandQuery,
  useCalculateBrandEquityMutation,
} from '../../../services/nestjs-base-api';
import type {
  IBrand,
  ICalculateBrandEquityRequest,
  ISurveyCohort,
} from '../../../services/nestjs-base-api/types';
import { PeriodEnum } from '../../../services/nestjs-base-api/types';

export type PeriodType =
  | 'last30'
  | 'last60'
  | 'last90'
  | 'last120'
  | 'last180'
  | 'last365'
  | 'allTime'
  | 'custom';

/** The mutable scope a marketer can change (brand is via the URL). */
export interface MarketerScope {
  periodType: PeriodType;
  dateFrom?: string;
  dateTo?: string;
  cohortIds: number[];
  countryCodes: string[];
}

/** Shape persisted in `brandEquityFilters_${userId}` (shared with the rest of the app). */
interface PersistedFilters {
  brandId?: number;
  cohortIds?: number[];
  countryCodes?: string[];
  periodType?: PeriodType;
  dateFrom?: string;
  dateTo?: string;
}

interface MarketerScopeContextValue {
  brands: IBrand[];
  activeBrand: IBrand | undefined;
  /** Switch brand — callers should navigate to the new brand's hash route. */
  brandsLoading: boolean;
  scope: MarketerScope;
  setScope: (next: Partial<MarketerScope>) => void;
  resetScope: () => void;
  cohorts: ISurveyCohort[];
  /** Calculate-endpoint payload derived from the active brand + scope. */
  payload: ICalculateBrandEquityRequest | undefined;
  /** Total responses in the active data window (for the scope pill). */
  totalResponses?: number;
  /** When the active BE result was calculated (ISO). */
  calculatedAt?: string;
}

const DEFAULT_SCOPE: MarketerScope = {
  periodType: 'last180',
  cohortIds: [],
  countryCodes: [],
};

export function periodTypeToEnum(periodType: PeriodType): PeriodEnum {
  switch (periodType) {
    case 'last30':
      return PeriodEnum.THIRTY_DAYS;
    case 'last60':
      return PeriodEnum.SIXTY_DAYS;
    case 'last90':
      return PeriodEnum.NINETY_DAYS;
    case 'last120':
      return PeriodEnum.ONE_TWENTY_DAYS;
    case 'last180':
      return PeriodEnum.ONE_EIGHTY_DAYS;
    case 'last365':
      return PeriodEnum.THREE_SIXTY_FIVE_DAYS;
    case 'allTime':
      return PeriodEnum.ALL_TIME;
    case 'custom':
      return PeriodEnum.DATE_RANGE;
    default:
      return PeriodEnum.ONE_EIGHTY_DAYS;
  }
}

/** Short English label for a period — single source for the pill + BE card chip. */
const PERIOD_SHORT_LABEL: Record<PeriodType, string> = {
  last30: 'Last 30 days',
  last60: 'Last 60 days',
  last90: 'Last 90 days',
  last120: 'Last 120 days',
  last180: 'Last 180 days',
  last365: 'Last 365 days',
  allTime: 'All time',
  custom: 'Custom range',
};

export function periodTypeToShortLabel(periodType: PeriodType): string {
  return PERIOD_SHORT_LABEL[periodType] ?? PERIOD_SHORT_LABEL.last180;
}

/**
 * Map the scope period to the `{N}_days` / `all_time` string used by the
 * Benchmarking (`timePeriod`) and Brand Analytics (`period`) endpoints, so the
 * marketer-view Benchmarks + Trends tabs compute Brand Equity over the SAME
 * window as the Briefing/Detail (SYR-68 data-consistency fix). `custom` ranges
 * aren't representable by those endpoints, so fall back to the 180-day default.
 */
export function periodTypeToDaysString(periodType: PeriodType): string {
  switch (periodType) {
    case 'last30':
      return '30_days';
    case 'last60':
      return '60_days';
    case 'last90':
      return '90_days';
    case 'last120':
      return '120_days';
    case 'last180':
      return '180_days';
    case 'last365':
      return '365_days';
    case 'allTime':
      return 'all_time';
    case 'custom':
    default:
      return '180_days';
  }
}

const MarketerScopeContext = createContext<MarketerScopeContextValue | null>(
  null,
);

interface MarketerScopeProviderProps {
  /** Brand hash from the `:brandHash` route param (URL is the brand source of truth). */
  brandHash?: string;
  children: React.ReactNode;
}

export const MarketerScopeProvider: React.FunctionComponent<
  MarketerScopeProviderProps
> = ({ brandHash, children }) => {
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const storageKey = user?.id ? `brandEquityFilters_${user.id}` : null;

  const { data: brandsData, isLoading: brandsLoading } = useGetBrandsQuery(
    { jwtToken, organisationId },
    { skip: !jwtToken || !organisationId },
  );
  const brands = useMemo(() => brandsData ?? [], [brandsData]);

  // Resolve the active brand from the URL hash; fall back to first brand.
  const activeBrand = useMemo(() => {
    if (brands.length === 0) return undefined;
    if (brandHash) {
      const match = brands.find((b) => b.hash === brandHash);
      if (match) return match;
    }
    return brands[0];
  }, [brands, brandHash]);

  // Hydrate scope from the shared localStorage filters once we know the user.
  const [scope, setScopeState] = useState<MarketerScope>(() => {
    if (typeof window === 'undefined' || !user?.id) return DEFAULT_SCOPE;
    try {
      const raw = localStorage.getItem(`brandEquityFilters_${user.id}`);
      if (!raw) return DEFAULT_SCOPE;
      const parsed = JSON.parse(raw) as PersistedFilters;
      return {
        periodType: parsed.periodType ?? DEFAULT_SCOPE.periodType,
        dateFrom: parsed.dateFrom,
        dateTo: parsed.dateTo,
        cohortIds: parsed.cohortIds ?? [],
        countryCodes: parsed.countryCodes ?? [],
      };
    } catch {
      return DEFAULT_SCOPE;
    }
  });

  // Cohorts for the active brand (drives the scope drawer's cohort select).
  const { data: cohortsData } = useGetCohortsByBrandQuery(
    { jwtToken, organisationId, brandId: activeBrand?.id ?? 0 },
    { skip: !jwtToken || !organisationId || !activeBrand?.id },
  );
  const cohorts = useMemo(() => cohortsData ?? [], [cohortsData]);

  // Persist scope + active brand back to the shared key on every change.
  useEffect(() => {
    if (!storageKey) return;
    const persisted: PersistedFilters = {
      brandId: activeBrand?.id,
      periodType: scope.periodType,
      dateFrom: scope.dateFrom,
      dateTo: scope.dateTo,
      cohortIds: scope.cohortIds,
      countryCodes: scope.countryCodes,
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(persisted));
    } catch {
      /* ignore quota / serialisation errors */
    }
  }, [storageKey, activeBrand?.id, scope]);

  const setScope = useCallback((next: Partial<MarketerScope>) => {
    setScopeState((prev) => ({ ...prev, ...next }));
  }, []);

  const resetScope = useCallback(() => {
    setScopeState(DEFAULT_SCOPE);
  }, []);

  const payload = useMemo<ICalculateBrandEquityRequest | undefined>(() => {
    if (!activeBrand) return undefined;
    const body: ICalculateBrandEquityRequest = {
      brandIds: [activeBrand.id],
      period: periodTypeToEnum(scope.periodType),
      skipAiAnalysis: true,
    };
    if (scope.cohortIds.length > 0) body.cohortIds = scope.cohortIds;
    if (scope.countryCodes.length > 0) body.countryCodes = scope.countryCodes;
    if (scope.periodType === 'custom' && scope.dateFrom && scope.dateTo) {
      body.dateFrom = scope.dateFrom;
      body.dateTo = scope.dateTo;
    }
    return body;
  }, [activeBrand, scope]);

  // Resolve the active data window's response count + calculated time for the
  // scope pill. Uses the same (server-cached) calculate as the destinations.
  const [calculate] = useCalculateBrandEquityMutation();
  const [beMeta, setBeMeta] = useState<
    { totalResponses: number; calculatedAt: string } | undefined
  >();
  useEffect(() => {
    let cancelled = false;
    if (!payload || !jwtToken || !organisationId) {
      setBeMeta(undefined);
      return;
    }
    calculate({ jwtToken, organisationId, payload })
      .unwrap()
      .then((res) => {
        if (!cancelled) {
          setBeMeta({
            totalResponses: res.totalResponses,
            calculatedAt: res.calculatedAt,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setBeMeta(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [payload, jwtToken, organisationId, calculate]);

  const value = useMemo<MarketerScopeContextValue>(
    () => ({
      brands,
      activeBrand,
      brandsLoading,
      scope,
      setScope,
      resetScope,
      cohorts,
      payload,
      totalResponses: beMeta?.totalResponses,
      calculatedAt: beMeta?.calculatedAt,
    }),
    [
      brands,
      activeBrand,
      brandsLoading,
      scope,
      setScope,
      resetScope,
      cohorts,
      payload,
      beMeta,
    ],
  );

  return (
    <MarketerScopeContext.Provider value={value}>
      {children}
    </MarketerScopeContext.Provider>
  );
};

export function useMarketerScope(): MarketerScopeContextValue {
  const ctx = useContext(MarketerScopeContext);
  if (!ctx) {
    throw new Error(
      'useMarketerScope must be used within a MarketerScopeProvider',
    );
  }
  return ctx;
}
